import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ORDER_STEPS } from '@/pages/Order/Order';
import useLocalCart from '@/hooks/useLocalCart';
import { useCallback } from 'react';
import { useModalActions } from '../useModalActions';
import { MODAL_KEY } from '@/constants';
import { motion } from 'framer-motion';
import { MessageSquareWarning, Trash2 } from 'lucide-react';
import { WithModalClose } from '../ModalFactory';

export default ({
  id,
  withModalClose,
}: {
  id: string;
  withModalClose: WithModalClose;
}) => {
  const { cartDict, hasBundle, updateLineItemQuantity, removeLineItem } =
    useLocalCart();
  const [searchParams, setSearchParams] = useSearchParams();

  const location = useLocation();
  const navigate = useNavigate();
  const productMeta = cartDict[id];

  // Detect if this line item has nested contents
  const isBoxShell = productMeta?.contents && productMeta.contents.length > 0;

  // Navigate to edit box
  const handleEditBox = withModalClose(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('id', productMeta.localId);
    newParams.set('box', productMeta.id); // Assuming you track the box ID
    newParams.set('mix', productMeta.mixType!);
    newParams.set('step', ORDER_STEPS[2]); // Assuming Step 2 is the builder
    setSearchParams(newParams, { replace: true });

    // 1. Check if we are already on the target Order page
    // (Adjust '/order' if your exact route path differs, e.g., '/' or '/menu')
    if (location.pathname.includes('/order')) {
      // We are on the right page, smoothly update the URL parameters without a hard navigation
      setSearchParams(newParams, { replace: true });
    } else {
      // 2. We are on a different page. Hard-navigate to the order page with the payload attached.
      navigate(`/order?${newParams.toString()}`);
    }
  });

  return (
    <div className="flex items-start gap-3 py-3 border-b border-brand-truffle/5 last:border-0 group">
      {/* SHRUNK: Image Placeholder (w-12 h-12) */}
      <div className="w-12 h-12 bg-brand-cream/80 rounded-[8px] flex items-center justify-center border border-brand-truffle/5 shrink-0 overflow-hidden">
        {productMeta?.img?.url ? (
          <motion.img
            src={productMeta?.img?.url}
            alt={productMeta?.img?.altText}
            className="w-full h-full object-contain relative z-10 drop-shadow-[0_20px_30px_rgba(0,0,0,0.15)] will-change-transform"
            // Subtle floating effect on hover
            whileHover={{ y: -8 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            loading="eager" // Eager loading since it's above the fold
          />
        ) : (
          <span className="text-text-muted/30 font-display text-[9px] tracking-widest uppercase">
            {productMeta.productType}
          </span>
        )}
      </div>

      {/* Product Details */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex justify-between items-start w-full">
          <span className="text-text-heading text-[13px] font-[700] tracking-wide leading-tight truncate pr-2">
            {productMeta.title}
          </span>
          {/* ADDED: Missing Price Anchor */}
          <span className="text-text-heading text-[13px] font-[700]">
            ${(productMeta?.price * productMeta.quantity).toFixed(2)}
          </span>
        </div>

        {/* Box Contents Sub-list & Edit Button */}
        {isBoxShell ? (
          <div className="mt-1 flex flex-col items-start">
            <div className="flex flex-col gap-0.5 mb-1.5">
              {productMeta?.contents?.map((subItem: any, idx: number) => (
                <span
                  key={idx}
                  className="text-[10px] text-text-muted leading-none flex items-center gap-1"
                >
                  <span className="font-semibold">{subItem.quantity}x</span>{' '}
                  {subItem.title}
                </span>
              ))}
            </div>
            <button
              onClick={handleEditBox}
              className="text-[10px] font-bold text-brand-burgundy uppercase tracking-widest hover:underline opacity-80 hover:opacity-100 transition-opacity"
            >
              Edit Contents
            </button>
          </div>
        ) : (
          /* Standard Metadata (Calories/Allergens) */
          <div className="w-full mt-0.5 truncate">
            {productMeta?.nutritionFacts?.calories && (
              <span className="text-text-muted text-[10px] font-body">
                {productMeta?.nutritionFacts?.calories} cal
              </span>
            )}
          </div>
        )}

        {/* COMPACT Quantity Controls */}
        <div className="flex items-center justify-start mt-2 h-7">
          {isBoxShell ? (
            <span className="font-body font-semibold text-[11px] text-text-muted">
              Qty: {productMeta.quantity}
            </span>
          ) : (
            <div className="flex items-center justify-between bg-brand-cream border border-brand-truffle/20 text-brand-truffle rounded-full h-7 px-1 w-full max-w-[80px]">
              <button
                onClick={() =>
                  updateLineItemQuantity(id, productMeta.quantity - 1)
                }
                className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-brand-truffle/5"
              >
                -
              </button>
              <span className="font-body font-bold text-[11px] w-4 text-center">
                {productMeta.quantity}
              </span>
              <button
                onClick={() =>
                  updateLineItemQuantity(id, productMeta.quantity + 1)
                }
                className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-brand-truffle/5"
              >
                +
              </button>
            </div>
          )}
          <motion.button
            onClick={() => removeLineItem(id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Remove item from cart"
            title="Remove item"
            className="p-2 ml-auto group flex items-center justify-center rounded-full text-brand-truffle/40 hover:text-red-500 hover:bg-red-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500/50"
          >
            <Trash2
              size={18}
              strokeWidth={2}
              className="transition-transform duration-200 group-hover:-rotate-12 group-hover:scale-110"
            />
          </motion.button>
        </div>
        {productMeta.quantity < 2 && !hasBundle && !isBoxShell && (
          <span className="mt-3 text-brand-error rounded-full px-2 inline-flex gap-2 font-display font-[500] text-[0.7rem] min-w-0 w-fit self-start items-center justify-center">
            <MessageSquareWarning size={'0.8rem'} color="#AD4B44" />
            {'Minimum (2) required.'}
          </span>
        )}
      </div>
    </div>
  );
};
